"use strict";
/**
 * @name Combobox
 *
 * @description
 *  A common combobox component that will render the appropriate styles. This calls the shared components InputControl
 *  with all the appropriate options. See the teamsnap patterns library for more information.
 *    https://teamsnap-ui-patterns.netlify.com/patterns/components/checkbox.html
 *
 * @example
 *  <Combobox
 *    name="birthdate"
 *    buttonLabel="Birthdate"
 *    searchLabel="Search for a year"
 *    items={["2006", "2007", "2008", "2009", "2010", "2011", "2012", "2013"]} />
 *
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = __importStar(require("react"));
var PropTypes = __importStar(require("prop-types"));
var helpers_1 = require("../../utils/helpers");
var Panel_1 = require("../Panel");
var PanelFooter_1 = require("../PanelFooter");
var PanelBody_1 = require("../PanelBody");
var PanelRow_1 = require("../PanelRow");
var Button_1 = require("../Button");
var Checkbox_1 = require("../Checkbox");
var Field_1 = require("../Field");
var Icon_1 = require("../Icon");
var index_1 = require("../Popup/index");
var PopupTooltip_1 = require("../Popup/PopupTooltip");
var propTypes = {
    name: PropTypes.string.isRequired,
    buttonLabel: PropTypes.string,
    searchLabel: PropTypes.node,
    items: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
        subtext: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    })),
    selected: PropTypes.arrayOf(PropTypes.any),
    onChange: PropTypes.func,
    className: PropTypes.string,
    mods: PropTypes.string,
    style: PropTypes.object,
    testId: PropTypes.string,
    otherProps: PropTypes.object,
    disabled: PropTypes.bool,
    tooltip: PropTypes.element,
    tooltipLink: PropTypes.string,
    showFromHyperlink: PropTypes.bool,
    canCancel: PropTypes.bool,
    doneButtonText: PropTypes.string,
    showSelectAll: PropTypes.bool,
    searchPlaceholder: PropTypes.string,
    onHoverText: PropTypes.string,
};
var TooltipWrapper = function (_a) {
    var tooltipLink = _a.tooltipLink, children = _a.children;
    if (!tooltipLink) {
        return React.createElement(React.Fragment, null, children);
    }
    return (React.createElement("a", { href: tooltipLink, target: "_blank", rel: "noreferrer" }, children));
};
TooltipWrapper.propTypes = {
    children: PropTypes.node.isRequired,
    tooltipLink: PropTypes.string,
};
var ComboBox = function (_a) {
    var name = _a.name, buttonLabel = _a.buttonLabel, style = _a.style, testId = _a.testId, otherProps = _a.otherProps, disabled = _a.disabled, className = _a.className, selected = _a.selected, mods = _a.mods, items = _a.items, onChange = _a.onChange, tooltip = _a.tooltip, tooltipLink = _a.tooltipLink, showFromHyperlink = _a.showFromHyperlink, canCancel = _a.canCancel, doneButtonText = _a.doneButtonText, showSelectAll = _a.showSelectAll, searchPlaceholder = _a.searchPlaceholder, onHoverText = _a.onHoverText;
    var _b = React.useState(false), initialized = _b[0], setInitialized = _b[1];
    var _c = React.useState(false), flyoutVisible = _c[0], toggleFlyout = _c[1];
    var _d = React.useState(''), comboLabel = _d[0], setComboLabel = _d[1];
    var _e = React.useState(false), hasFilters = _e[0], setHasFilters = _e[1];
    var _f = React.useState(''), searchParam = _f[0], setSearchParam = _f[1];
    var _g = React.useState([]), filterList = _g[0], setFilterList = _g[1];
    var _h = React.useState([]), uncheckedfilterList = _h[0], setUncheckedFilterList = _h[1];
    var _j = React.useState([]), selectedFilters = _j[0], setSelectedFilters = _j[1];
    var shouldShowSearchBar = items.length > 6;
    var filteredItems = searchParam
        ? uncheckedfilterList.filter(function (item) {
            return item.label.toLowerCase().includes(searchParam.toLowerCase());
        })
        : uncheckedfilterList;
    var createLabel = function (comboboxItems) {
        return comboboxItems
            .reduce(function (prev, current) { return __spreadArray(__spreadArray([], prev, true), [items.find(function (item) { return item.value === current.value; }).label], false); }, [])
            .join(', ');
    };
    var sortFilters = function (currentFilters) {
        var checked = [];
        var unchecked = [];
        items.forEach(function (item) {
            if (currentFilters.includes(item.value)) {
                checked.push(item);
            }
            else {
                unchecked.push(item);
            }
        });
        setComboLabel(checked.length > 0 && !showFromHyperlink ? createLabel(checked) : buttonLabel);
        setFilterList(checked);
        setUncheckedFilterList(unchecked);
        if (currentFilters.length > 0) {
            setHasFilters(true);
        }
        else {
            setHasFilters(false);
        }
    };
    var clearFilters = function () {
        setSelectedFilters([]);
        setHasFilters(false);
        onChange([]);
        setSearchParam('');
        setComboLabel(buttonLabel);
    };
    var filtersFromPropsAreDifferent = function (fromProps, currentFilters) {
        return ((fromProps === null || fromProps === void 0 ? void 0 : fromProps.length) !== (currentFilters || []).length ||
            fromProps.some(function (item) { return !(currentFilters || []).includes(item); }));
    };
    React.useEffect(function () {
        sortFilters(selected);
        setSelectedFilters(selected);
    }, [selected]);
    React.useEffect(function () {
        setSelectedFilters(selected);
        sortFilters(selected);
        setInitialized(true);
    }, []);
    var handleBodyClick = React.useCallback(function (e) {
        e.stopPropagation();
        var isTargetingPopup = e.target.closest("#Combobox-".concat(name)) != null;
        if (!isTargetingPopup) {
            toggleFlyout(false);
        }
    }, []);
    React.useEffect(function () {
        if (!flyoutVisible) {
            if (initialized && filtersFromPropsAreDifferent(selected, selectedFilters) && !canCancel) {
                onChange(selectedFilters);
            }
            window.removeEventListener('click', handleBodyClick, false);
        }
        else {
            window.addEventListener('click', handleBodyClick, false);
        }
    }, [flyoutVisible]);
    var buildCheckbox = function (filter, idx) {
        return (React.createElement(Checkbox_1.Checkbox, { key: "".concat(name, "-").concat(idx), testId: "".concat(name, "-").concat(idx), mods: "".concat(idx === items.length - 1 ? 'u-padBottomNone' : ''), name: filter.value, label: filter.subtext ? (React.createElement(React.Fragment, null,
                filter.label,
                " ",
                React.createElement("span", { style: { color: '#7a7a7a' } },
                    "(",
                    filter.subtext,
                    ")"))) : (filter.label), inputProps: {
                checked: selectedFilters.includes(filter.value),
                value: filter.value,
                onChange: function () {
                    if (filter.value === 'select-all') {
                        var newList = filteredItems
                            .map(function (i) { return i.value; })
                            .concat(filterList.map(function (i) { return i.value; }));
                        var index = selectedFilters.indexOf('select-all');
                        if (index === -1) {
                            newList.push('select-all');
                        }
                        else {
                            newList = [];
                        }
                        setSelectedFilters(newList);
                    }
                    else if (selectedFilters.includes(filter.value)) {
                        setSelectedFilters(selectedFilters.filter(function (selectedFilter) { return selectedFilter !== filter.value; }));
                    }
                    else {
                        setSelectedFilters(__spreadArray(__spreadArray([], selectedFilters, true), [filter.value], false));
                    }
                },
            } }));
    };
    return (React.createElement("div", __assign({ id: "Combobox-".concat(name), className: (0, helpers_1.getClassName)(className, mods), style: style, "data-testid": testId }, otherProps),
        React.createElement("button", { type: "submit", className: showFromHyperlink
                ? "Combobox-hyperlink ".concat(disabled ? 'u-colorNeutral6' : 'u-colorPrimary6')
                : "Combobox-toggle ".concat(hasFilters ? 'Combobox-toggle--active' : ''), name: name, id: name, "data-testid": "comboboxButton", disabled: disabled, title: onHoverText && disabled ? onHoverText : comboLabel !== null && comboLabel !== void 0 ? comboLabel : '', onClick: function () { return toggleFlyout(!flyoutVisible); } }, comboLabel !== null && comboLabel !== void 0 ? comboLabel : buttonLabel),
        flyoutVisible && (React.createElement(Panel_1.Panel, { mods: "Combobox-checkboxContainer", "data-testid": "flyout" },
            React.createElement(PanelBody_1.PanelBody, { mods: "Combobox-checkboxes" },
                shouldShowSearchBar && (React.createElement(PanelRow_1.PanelRow, { mods: "Grid-cell u-flexAuto u-padBottomMd" },
                    React.createElement(Field_1.Field, { type: "input", formFieldProps: {
                            inputProps: {
                                value: searchParam,
                                onChange: function (e) { return setSearchParam(e.target.value); },
                            },
                            placeholder: searchPlaceholder || "Search for a ".concat(buttonLabel),
                            leftIcon: React.createElement(Icon_1.Icon, { className: "Icon", name: "search" }),
                        }, name: "Sample" }))),
                showSelectAll && items.length > 1 && (React.createElement(PanelRow_1.PanelRow, null, buildCheckbox({ value: 'select-all', label: 'Select All' }, 0))),
                (filterList === null || filterList === void 0 ? void 0 : filterList.length) > 0 && (React.createElement(PanelRow_1.PanelRow, { mods: "Grid-cell u-flexAuto u-padBottomSm ".concat(showSelectAll ? 'u-padLeftXl' : '') }, filterList.map(function (item, idx) { return buildCheckbox(item, idx); }))),
                (filteredItems === null || filteredItems === void 0 ? void 0 : filteredItems.length) > 0 && (React.createElement(PanelRow_1.PanelRow, { mods: showSelectAll ? 'u-padLeftXl' : '' }, filteredItems.map(function (item, idx) { return buildCheckbox(item, idx); }))),
                (filteredItems === null || filteredItems === void 0 ? void 0 : filteredItems.length) === 0 && (filterList === null || filterList === void 0 ? void 0 : filterList.length) === 0 && (React.createElement(PanelRow_1.PanelRow, null, "No options to display."))),
            React.createElement(PanelFooter_1.PanelFooter, { mods: "u-padEndsSm u-padSidesMd" },
                React.createElement("div", { className: "u-flex u-flexAlignItemsCenter u-flexJustifyBetween" },
                    React.createElement("div", { className: "u-padLeftXs" }, tooltip && (React.createElement(index_1.PopupTooltip, { text: tooltip, variant: PopupTooltip_1.PopupTooltipVariant.LIGHT },
                        React.createElement(TooltipWrapper, { tooltipLink: tooltipLink },
                            React.createElement(Icon_1.Icon, { mods: "u-colorPrimary5", name: "info" }))))),
                    React.createElement("div", null,
                        React.createElement(Button_1.Button, { onClick: function () {
                                if (!canCancel) {
                                    clearFilters();
                                }
                                toggleFlyout(false);
                            }, mods: "u-spaceRightMd u-colorNeutral7", type: "link", testId: "combobox-cancel-button" }, canCancel ? 'Cancel' : 'Clear'),
                        React.createElement(Button_1.Button, { onClick: function () {
                                toggleFlyout(false);
                                if (canCancel) {
                                    onChange(selectedFilters);
                                }
                            }, type: "link", testId: "combobox-done-button" }, doneButtonText))))))));
};
ComboBox.propTypes = propTypes;
ComboBox.defaultProps = {
    mods: null,
    style: {},
    testId: null,
    className: 'Combobox',
    otherProps: {},
    selected: [],
    showFromHyperlink: false,
    canCancel: false,
    doneButtonText: 'Done',
    showSelectAll: false,
    searchPlaceholder: null,
    onHoverText: '',
};
exports.default = ComboBox;
